import React, { useEffect, useState } from "react";
import { heatColor, reachedColors, reachedTick } from "../utils/heatColor";
import { gaugeGeometry } from "../utils/gaugeGeometry";
import { useRenderTier } from "../hooks/useRenderTier";

// Halve meter met het teampercentage; alles beweegt via CSS-transitions.
// De gloed (dure SVG-blur) hangt af van de kwaliteitstrap, fx.gaugeGlow:
//   full  - wijde en strakke gloed plus zachte kop
//   light - alleen de strakke gloed
//   off   - geen filters, een doorschijnende brede band als vervanger

const sweepEase = "1.4s cubic-bezier(0.22, 1, 0.36, 1)"; // snel weg, zacht landen

// De glans als het target gehaald is: drie keer dezelfde streep, smaller en feller naar
// het midden, zodat hij zacht aan- en uitloopt zoals die op de balk (die een verloop heeft).
// Een SVG-streep kan zelf geen verloop in de lengte, en een blur kost de Pi te veel.
const glintLayers = [
  { className: "is-outer", alpha: 0.22 },
  { className: "is-mid", alpha: 0.3 },
  { className: "is-core", alpha: 0.5 },
];

// Start op 0 en zet de waarde een frame later, zodat de meter openveegt.
function useSweepIn(target) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(target));
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return shown;
}

const ArcGauge = ({ percent = 0, size = 400, stroke = 26, reached = false }) => {
  const { fx } = useRenderTier();
  const glow = fx.gaugeGlow;
  const target = Math.min(100, Math.max(0, percent));
  const shown = useSweepIn(target);
  // Boven het target lopen de streepjes een tweede ronde: wit tot 200%.
  const over = useSweepIn(reached ? Math.min(100, Math.max(0, percent - 100)) : 0);

  const progress = shown / 100;
  const { cx, cy, radius, height, path, length, dashOffset, ticks } =
    gaugeGeometry(size, stroke, progress);

  const hot = heatColor(shown);
  const mid = heatColor(shown * 0.72);
  const base = heatColor(18 + shown * 0.26);

  // Legt de heetste kleur van het rechte verloop precies onder de kop.
  const tipStop = Math.max(0.02, (1 - Math.cos(Math.PI * progress)) / 2);

  // Kwadratisch, zodat de gloed pas richting target echt oploopt.
  const heat = progress * progress;
  const bloomOpacity = 0.05 + 0.42 * heat;
  const glowOpacity = 0.2 + 0.22 * progress;
  // Gehaald: vlak wit, zonder gloed en zonder kop.
  const lit = target > 0.5 && !reached;

  const sweep = { transition: `stroke-dashoffset ${sweepEase}` };
  const dash = {
    strokeDasharray: `${length} ${length}`,
    strokeDashoffset: dashOffset,
  };

  return (
    <svg
      className="gauge"
      width={size}
      height={height}
      viewBox={`0 0 ${size} ${height}`}
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient id="gaugeHeat" x1="0" y1="0" x2="1" y2="0">
          {reached ? (
            reachedColors.map((color, index) => (
              <stop
                key={color}
                offset={index / (reachedColors.length - 1)}
                stopColor={color}
              />
            ))
          ) : (
            <>
              <stop offset="0" stopColor={base} />
              <stop offset={tipStop * 0.55} stopColor={mid} />
              <stop offset={tipStop} stopColor={hot} />
              <stop offset="1" stopColor={hot} />
            </>
          )}
        </linearGradient>

        {/* Ruime filtervlakken, anders knipt de blur af. */}
        <filter id="gaugeBloom" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={9 + 6 * progress} />
        </filter>
        <filter id="gaugeGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id="gaugeHead" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation={5 + 5 * progress} />
        </filter>
      </defs>

      <g className="gauge-scale">
        {ticks.map((tick, i) => (
          <line
            key={i}
            className={`gauge-tick${tick.major ? " is-major" : ""}`}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke={
              !reached
                ? tick.color
                : over > 0 && i / (ticks.length - 1) <= over / 100
                  ? "#ffffff"
                  : reachedTick
            }
            strokeWidth={tick.width}
            strokeLinecap="round"
            style={{ transitionDelay: `${tick.delay}s` }}
          />
        ))}
      </g>

      {/* Lege baan */}
      <path
        d={path}
        fill="none"
        stroke="rgba(255, 255, 255, 0.14)"
        strokeWidth={stroke}
        strokeLinecap="round"
      />

      {/* Wijde gloed, alleen over het gevulde stuk. */}
      {lit && glow === "full" && (
        <path
          className="gauge-bloom"
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 22}
          strokeLinecap="round"
          filter="url(#gaugeBloom)"
          {...dash}
          style={{ "--flame": bloomOpacity, ...sweep }}
        />
      )}

      {/* Zonder filters: doorschijnende band als goedkope gloed. */}
      {lit && glow === "off" && (
        <path
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 16}
          strokeLinecap="round"
          opacity={0.16 + 0.12 * progress}
          {...dash}
          style={sweep}
        />
      )}

      {/* Strakke gloed vlak om de band */}
      {lit && glow !== "off" && (
        <path
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 7}
          strokeLinecap="round"
          filter="url(#gaugeGlow)"
          opacity={glowOpacity}
          {...dash}
          style={sweep}
        />
      )}

      {/* Voortgang */}
      <path
        d={path}
        fill="none"
        stroke="url(#gaugeHeat)"
        strokeWidth={stroke}
        strokeLinecap="round"
        {...dash}
        style={sweep}
      />

      {/* Gehaald: een witte glans die af en toe over het goud glijdt, zie .gauge-glint. */}
      {reached &&
        glintLayers.map((layer) => (
          <path
            key={layer.className}
            className={`gauge-glint ${layer.className}`}
            d={path}
            pathLength="100"
            fill="none"
            stroke={`rgba(255, 255, 255, ${layer.alpha})`}
            strokeWidth={stroke}
          />
        ))}

      {/* De kop draait om het middelpunt, zodat hij de boog volgt. */}
      {target > 1 && !reached && (
        <g
          className="gauge-headgroup"
          style={{
            transformBox: "view-box",
            transformOrigin: `${cx}px ${cy}px`,
            transform: `rotate(${180 * progress}deg)`,
            transition: `transform ${sweepEase}`,
          }}
        >
          <circle
            cx={cx - radius}
            cy={cy}
            r={stroke * 0.7}
            fill={hot}
            opacity={0.3 + 0.4 * heat}
            filter={glow === "off" ? undefined : "url(#gaugeHead)"}
          />
          <circle cx={cx - radius} cy={cy} r={stroke * 0.3} fill="#ffffff" />
        </g>
      )}

      <text className="gauge-cap" x={cx - radius} y={cy + 32} textAnchor="middle">
        0
      </text>
      <text className="gauge-cap" x={cx + radius} y={cy + 32} textAnchor="middle">
        100
      </text>
    </svg>
  );
};

export default ArcGauge;
